<template>
  <base-slide-over ref="slideover" :title="isNew === true ? 'New Program' : 'Edit Program'" size="2xl" style="z-index: 10000">
    <base-form ref="programForm" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw:px-6" v-model="isFormValid">
      <base-text-field label="Program Name" placeholder="Enter program name" v-model="businessProgram.name" class="tw-mt-3" required :maxLength="255" />

      <base-select label="Program Group" placeholder="Select a group" :options="programGroups" v-model="businessProgram.programGroup" />
      <base-text-field
        v-if="businessProgram.programGroup === 'new group'"
        label="New Program Group"
        placeholder="Enter new program group"
        v-model="businessProgram.newProgramGroup"
        :rules="[() => (businessProgram.newProgramGroup !== null && businessProgram.newProgramGroup !== '') || 'Please enter a new group name']"
        :maxLength="255"
        class="tw-mt-6"
      />

      <base-select label="Program Category" placeholder="Select a category" :options="programCategories" v-model="businessProgram.programCategory" class="tw-mt-6" />
      <base-text-field
        v-if="businessProgram.programCategory === 'new category'"
        label="New Program Category"
        placeholder="Enter new program category"
        v-model="businessProgram.newProgramCategory"
        :rules="[() => (businessProgram.newProgramCategory !== null && businessProgram.newProgramCategory !== '') || 'Please enter a new category name']"
        :maxLength="255"
        class="tw-mt-6"
      />

      <base-text-field
        label="Program Medication Field Label"
        placeholder="Enter field label (e.g., Medication)"
        v-model="businessProgram.programFieldLabel"
        class="tw-mt-6"
        :maxLength="50"
        required
      />

      <base-text-field
        label="Max Medications"
        placeholder="Enter the maximum medications for this program"
        v-model="businessProgram.maxMedications"
        required
        type="number"
        min="0"
        step="1"
        class="tw-mt-2"
      />

      <base-advanced-select
        class="tw-my-5"
        label="Allowed Medications"
        placeholder="Select allowed medications"
        :options="groupedMedicationOptions"
        v-model="businessProgram.allowedMedications"
        label-key="name"
        multiple
        :rules="[() => businessProgram.allowedMedications.length > 0 || 'Please choose at least one medication.']"
      />

      <div class="tw-mt-4 tw-pt-4">
        <!-- All Locations Toggle -->
        <div class="tw-flex tw-items-center tw-mb-2">
          <div class="tw-text-sm tw-mr-2 tw-my-auto tw-text-gray-800 tw-cursor-pointer">All Locations</div>
          <base-toggle v-model="allLocationsToggle" />
        </div>

        <!-- Include/Exclude Radios (only if not 'all') -->
        <div v-if="businessProgram.locationFilterType !== 'all'" class="tw-mt-4">
          <base-radio-group v-model="businessProgram.locationFilterType" class="tw-space-y-1" required>
            <base-radio value="include" name="locationFilterType" v-model="businessProgram.locationFilterType">Include Locations</base-radio>
            <base-radio value="exclude" name="locationFilterType" v-model="businessProgram.locationFilterType">Exclude Locations</base-radio>
          </base-radio-group>

          <base-advanced-select
            v-if="businessProgram.locationFilterType !== 'all'"
            label="Locations"
            placeholder="Select one or more locations"
            :options="locations"
            v-model="selectedLocations"
            label-key="name"
            multiple
          />
        </div>
      </div>
    </base-form>

    <inline-error id="business-program-error" :title="errorTitle" :message="errorMessage" :error="error" />

    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="close()">Cancel</base-button>
        <base-button :loading="isSaving" :disabled="!isFormValid" class="tw-w-auto" @click="save">Save</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";
import { orderBy } from "lodash";

export default {
  name: "BusinessProgramSlideover",
  props: {
    businessId: String,
  },
  components: { InlineError },
  data() {
    return {
      isFormValid: true,
      isSaving: false,
      isNew: false,
      businessProgram: {
        businessId: null,
        businessProgramId: null,
        name: null,
        maxMedications: null,
        programGroup: null,
        newProgramGroup: null,
        programCategory: null,
        newProgramCategory: null,
        programFieldLabel: "Medication",
        allowedMedications: [],
        locationFilterType: "all", // Default to 'all'
        locationsId: [], // To store selected location IDs
      },
      allLocationsToggle: true, // Local toggle state
      locationFilterOptions: [
        { value: "include", label: "Include Locations" },
        { value: "exclude", label: "Exclude Locations" },
      ],
      selectedLocations: [], // Array of selected location objects (for the dropdown)
      programGroups: [],
      programCategories: [],
      medicationOptions: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    locations: mapGetters("location", ["locations"]).locations, // Access the getter's value directly
    ...mapGetters("organization", ["organization"]),
    groupedMedicationOptions() {
      const options = [];
      const groups = [...new Set(this.medicationOptions.map((item) => item.productGroup))];

      groups.forEach((g) => {
        options.push({ name: g, isHeader: true });
        options.push(
          ...orderBy(
            this.medicationOptions.filter((p) => p.productGroup === g),
            ["name"],
          ),
        );
      });

      return options;
    },
  },
  watch: {
    allLocationsToggle(newValue) {
      if (newValue) {
        this.businessProgram.locationFilterType = "all";
        this.selectedLocations = [];
      } else {
        if (this.businessProgram.locationFilterType === "all") {
          this.businessProgram.locationFilterType = "include"; // Default to 'include' when toggled off
        }
      }
    },
    "businessProgram.locationFilterType"(newVal) {
      // Ensure toggle reflects current type
      this.allLocationsToggle = newVal === "all";
    },
    locations: {
      handler(newVal) {
        if (this.businessProgram.locationsId) {
          try {
            const parsed = JSON.parse(this.businessProgram.locationsId);
            this.selectedLocations = newVal.filter((loc) => parsed.includes(loc.businessLocationId));
          } catch (e) {
            this.selectedLocations = [];
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    reset() {
      this.isNew = false;
      this.businessProgram = {
        businessId: null,
        businessProgramId: null,
        name: null,
        maxMedications: null,
        programGroup: null,
        newProgramGroup: null,
        programCategory: null,
        newProgramCategory: null,
        programFieldLabel: "Medication",
        allowedMedications: [],
        locationFilterType: "all",
        locationsId: [],
      };
      this.allLocationsToggle = true;
      this.selectedLocations = [];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    show(businessId, businessProgram, programGroups, programCategories) {
      this.reset();
      this.programGroups = programGroups;
      this.programCategories = programCategories;
      this.getProgramCategories(businessId);

      if (this.medicationOptions.length === 0) {
        this.getMedications();
      }

      if (!businessProgram) {
        this.isNew = true;
      } else {
        this.businessProgram.businessId = businessId;
        this.businessProgram.businessProgramId = businessProgram.businessProgramId;
        this.businessProgram.name = businessProgram.name;
        this.businessProgram.maxMedications = businessProgram.maxMedications;
        this.businessProgram.programGroup = businessProgram.programGroup;
        this.businessProgram.programCategory = businessProgram.programCategory;
        this.businessProgram.allowedMedications = businessProgram.allowedMedications;
        this.businessProgram.programFieldLabel = businessProgram.programFieldLabel
          ? businessProgram.programFieldLabel.charAt(0).toUpperCase() + businessProgram.programFieldLabel.slice(1)
          : "Medication";
        this.allLocationsToggle = businessProgram.locationFilterType === "all";
        this.businessProgram.locationFilterType = businessProgram.locationFilterType || "all";

        // Save raw locationsId for watcher
        this.businessProgram.locationsId = businessProgram.locationsId || "[]";
        if (businessProgram.locationsId) {
          try {
            const parsed = JSON.parse(businessProgram.locationsId);
            this.selectedLocations = this.locations.filter((loc) => parsed.includes(loc.businessLocationId));
          } catch (e) {
            this.selectedLocations = [];
          }
        }
      }

      this.$refs.slideover.show();
    },
    close() {
      this.$refs.slideover.hide();
    },
    getMedications() {
      const url = `/businesses/${this.businessId}/item-categories/all`;
      this.$axios.get(url).then((res) => {
        this.medicationOptions = res.data.filter((i) => i.type === "medication");
      });
    },

    save() {
      if (!this.$refs.programForm.validate()) return;

      if (this.businessProgram.newProgramGroup) {
        this.businessProgram.programGroup = this.businessProgram.newProgramGroup;
      }

      if (this.businessProgram.newProgramCategory) {
        this.businessProgram.programCategory = this.businessProgram.newProgramCategory;
      }

      this.businessProgram.locationsId = JSON.stringify(this.selectedLocations.map((loc) => loc.businessLocationId));

      this.isSaving = true;

      const request = this.isNew
        ? this.$axios.post(`/businesses/${this.businessId}/programs`, this.businessProgram)
        : this.$axios.put(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}`, this.businessProgram);

      request
        .then(() => {
          this.close();
          this.$emit("save");
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: this.isNew ? "Program Added" : "Program Updated",
            message: `Program ${this.businessProgram.name} was ${this.isNew ? "created" : "updated"} successfully`,
          });
        })
        .catch((err) => {
          this.errorTitle = `Failed to ${this.isNew ? "create" : "update"} ${this.businessProgram.name}`;
          this.errorMessage = `Program ${this.businessProgram.name} was not able to be ${this.isNew ? "created" : "updated"}. Please try again.`;
          this.error = err;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    async getProgramCategories(businessId) {
      try {
        const url = `/businesses/${businessId}/programs/categories`;
        const res = await this.$axios.get(url);
        // Map the res.data array into categories, ensuring no null or undefined entries are included
        let categories = (res.data || [])
          .filter((item) => item !== null && item !== undefined) // Remove null or undefined items
          .map((category) => ({
            value: category,
            label: category,
          }));

        // Sort the categories alphabetically by their label (category value)
        categories.sort((a, b) => a.label.localeCompare(b.label));
        // Push the "+ Add New Category" option
        categories.push({ value: "new category", label: "+ Add New Category" });

        this.programCategories = categories;
      } catch (error) {
        console.error("Failed to fetch program categories:", error);
      }
    },
  },
};
</script>
